/**
 * Files bar: Files-tab items pinned under the bookmark bar for one-click upload into
 * websites. When a site asks for a file (its upload / choose-file button), the app's
 * picker opens instead: tick pinned files, add more from this device, upload together.
 */
import { useRef, useState } from "react";
import { toast } from "sonner";
import { File as FileIcon, FileText, Image as ImageIcon, FileSpreadsheet, X, Paperclip, Upload, CheckSquare, Square, HardDrive } from "lucide-react";
import api from "@/lib/api";

export const iconForMime = (m = "", name = "") => {
  const n = name.toLowerCase();
  if (m.startsWith("image/")) return ImageIcon;
  if (m.includes("pdf") || n.endsWith(".pdf")) return FileText;
  if (m.includes("sheet") || m.includes("csv") || /\.(xlsx?|csv)$/.test(n)) return FileSpreadsheet;
  return FileIcon;
};
const fmtSize = (b = 0) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

/** Fetch a pinned Files-tab item as a real File object the page can upload. */
export const pinToFile = async (p) => {
  const r = await api.get(`/files/${p.file_id}/download`, { responseType: "blob" });
  return new File([r.data], p.name, { type: p.mime_type || r.data.type || "application/octet-stream" });
};

export const FilesBar = ({ pins, onPick, onRemoved }) => (
  <div className="flex items-center gap-1 overflow-x-auto border-b border-white/10 px-2 pb-1.5" data-testid="browser-files-bar">
    <span className="mr-1 inline-flex shrink-0 items-center gap-1 font-mono text-[9px] uppercase tracking-widest text-white/40" title="Files pinned from the Files tab — click one to upload it to the open site"><Paperclip className="h-3 w-3" /> Files</span>
    {pins.map((p) => { const Icon = iconForMime(p.mime_type, p.name); return (
      <span key={p.pin_id} className="group flex max-w-[11rem] shrink-0 items-center rounded-md text-[11px] text-white/70 hover:bg-white/10 hover:text-white">
        <button type="button" onClick={() => onPick(p)} title={`${p.name} · ${fmtSize(p.size)} — click to upload to this site`} data-testid={`browser-file-${p.pin_id}`} className="flex min-w-0 items-center gap-1.5 px-2 py-1">
          <Icon className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{p.name}</span>
        </button>
        <button type="button" aria-label="Remove from files bar" data-testid={`browser-file-remove-${p.pin_id}`} className="mr-1 hidden rounded-full p-0.5 hover:bg-white/15 group-hover:block"
          onClick={async () => { await api.delete(`/browser/files/${p.pin_id}`); onRemoved(); }}><X className="h-3 w-3" /></button>
      </span>
    ); })}
  </div>
);

/** Replaces the site's native file chooser. `request` = {fid, multiple, accept} from the page. */
export const FilePickerDialog = ({ request, pins, preselect = [], onCancel, onSubmit }) => {
  const [chosen, setChosen] = useState(() => new Set(preselect));
  const [local, setLocal] = useState([]);
  const [busy, setBusy] = useState(false);
  const deviceRef = useRef(null);
  const multiple = !!request.multiple;
  const total = chosen.size + local.length;
  const tooMany = !multiple && total > 1;

  const toggle = (id) => setChosen((s) => { const n = new Set(multiple ? s : []); if (s.has(id)) n.delete(id); else n.add(id); return n; });
  const addLocal = (e) => { const fs = Array.from(e.target.files || []); setLocal((l) => (multiple ? [...l, ...fs] : fs.slice(0, 1))); if (!multiple && fs.length) setChosen(new Set()); e.target.value = ""; };
  const submit = async () => {
    setBusy(true);
    try {
      const fromBar = await Promise.all(pins.filter((p) => chosen.has(p.pin_id)).map(pinToFile));
      onSubmit([...fromBar, ...local]);
    } catch { toast.error("Could not load a pinned file — it may have been deleted from Files"); setBusy(false); }
  };
  const row = "flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-xs hover:bg-gray-100 dark:hover:bg-white/10";

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 p-4" onClick={onCancel} data-testid="browser-file-picker">
      <div className="flex max-h-[80vh] w-full max-w-md flex-col rounded-xl border border-gray-200 bg-white text-gray-900 shadow-2xl dark:border-white/15 dark:bg-[#15171b] dark:text-white" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-gray-200 px-4 py-3 dark:border-white/10">
          <h3 className="text-sm font-bold uppercase tracking-widest">Upload to this site</h3>
          <p className="font-mono text-[10px] text-gray-500 dark:text-white/50">{multiple ? "Pick as many as you need" : "This site accepts one file"}{request.accept ? ` · accepts ${request.accept}` : ""}</p>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
          <p className="px-2 py-1 font-mono text-[9px] uppercase tracking-widest text-gray-500 dark:text-white/50">From the files bar</p>
          {pins.length === 0 && <p className="px-2 py-2 text-xs text-gray-500 dark:text-white/50">Nothing pinned yet — in the Files tab, right-click (or long-press) a file → Add to browser.</p>}
          {pins.map((p) => { const Icon = iconForMime(p.mime_type, p.name); const on = chosen.has(p.pin_id); return (
            <button key={p.pin_id} type="button" onClick={() => toggle(p.pin_id)} data-testid={`browser-pick-${p.pin_id}`} className={`${row} ${on ? "bg-[#1a3fd1]/10 dark:bg-[#1a3fd1]/25" : ""}`}>
              {on ? <CheckSquare className="h-4 w-4 shrink-0 text-[#1a3fd1] dark:text-[#8fa8ff]" /> : <Square className="h-4 w-4 shrink-0 text-gray-400" />}
              <Icon className="h-4 w-4 shrink-0 text-gray-500 dark:text-white/60" />
              <span className="min-w-0 flex-1 truncate font-medium">{p.name}</span>
              <span className="font-mono text-[10px] text-gray-500 dark:text-white/50">{fmtSize(p.size)}</span>
            </button>
          ); })}
          <p className="mt-2 px-2 py-1 font-mono text-[9px] uppercase tracking-widest text-gray-500 dark:text-white/50">From this device</p>
          {local.map((f, i) => (
            <div key={`${f.name}-${i}`} className={row} data-testid={`browser-pick-local-${i}`}>
              <HardDrive className="h-4 w-4 shrink-0 text-gray-500 dark:text-white/60" /><span className="min-w-0 flex-1 truncate font-medium">{f.name}</span>
              <span className="font-mono text-[10px] text-gray-500 dark:text-white/50">{fmtSize(f.size)}</span>
              <button type="button" aria-label="Remove" onClick={() => setLocal((l) => l.filter((_, j) => j !== i))} className="rounded p-0.5 hover:bg-gray-200 dark:hover:bg-white/15"><X className="h-3 w-3" /></button>
            </div>
          ))}
          <button type="button" onClick={() => deviceRef.current?.click()} data-testid="browser-pick-device" className={`${row} border border-dashed border-gray-300 text-gray-700 dark:border-white/20 dark:text-white/80`}>
            <Upload className="h-4 w-4" /> Choose from this device…
          </button>
          <input ref={deviceRef} type="file" multiple={multiple} accept={request.accept || undefined} className="hidden" onChange={addLocal} data-testid="browser-pick-device-input" />
        </div>
        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 dark:border-white/10">
          {tooMany ? <span className="text-[11px] text-red-500">Pick just one — this site takes a single file</span> : <span className="font-mono text-[10px] text-gray-500 dark:text-white/50">{total} file{total === 1 ? "" : "s"} selected</span>}
          <span className="flex gap-2">
            <button type="button" onClick={onCancel} data-testid="browser-pick-cancel" className="rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 dark:border-white/15 dark:text-white/80">Cancel</button>
            <button type="button" onClick={submit} disabled={busy || total === 0 || tooMany} data-testid="browser-pick-submit" className="rounded-md bg-[#1a3fd1] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40">{busy ? "Loading…" : `Upload ${total || ""}`.trim()}</button>
          </span>
        </div>
      </div>
    </div>
  );
};
